import React, { useCallback, useMemo, useState } from 'react';
import { Collapse, Box, IconButton, Divider } from '@mui/material'; // הוסר Button של MUI (לא בשימוש)
import { AdvancedBtn, filterManagerHooks } from '.';
import { AutoComplete, InputBase, Button as SystemButton } from 'components/Form'; // הוסר Chip (לא בשימוש)
import { FilterManagerStyles, printPageStyle } from './Style';
import { tableHooks, ToolbarColumnsButton } from 'components/Table';
import { getAssessmentDefsFetch } from 'store/middlewares/assessment.crud';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { Close } from '@mui/icons-material';
import ReactToPrint from 'react-to-print';
import qc from 'keys/queryClient';

/**
 * FilterManager Component
 * * מנהל את סרגל הסינונים (פילטרים) מעל טבלאות, כולל סינונים ראשיים ומתקדמים.
 * * @param {Object} props
 * @param {Object} props.schema - סכימת הפילטרים שמכילה אובייקט `primary` ו-`advanced`
 * @param {Object} props.values - הסטייט הנוכחי של ערכי הפילטרים שנבחרו
 * @param {Function} props.onChange - פונקציה לעדכון ערכי הפילטרים אצל רכיב האב
 * @param {boolean} props.isExpanded - האם סרגל הסינון המתקדם פתוח
 * @param {Function} props.setIsExpanded - פונקציה לשינוי מצב הפתיחה של הסינון המתקדם
 * @param {React.ReactNode} [props.customToolbar] - כפתורים/תצוגה מותאמת אישית שיוצגו בסרגל
 * @param {Object} [props.Context] - קונטקסט (אופציונלי) לניהול חיפוש חופשי
 * @param {Object} [props.table] - רפרנס לטבלה עבור כפתור הסתרת העמודות
 * @param {Object} [props.wrapperRef] - רפרנס לרכיב העוטף כדי לאפשר הדפסה שלו
 */
const FilterManager = (props) => {
    const {
        customToolbar, Context, table, wrapperRef,
        schema, values, onChange, setIsExpanded, isExpanded
    } = props;

    const { headerFilters } = useSelector(state => state.header);

    // ניהול החיפוש החופשי (אם הועבר Context)
    const { searchInput, searchButton } = tableHooks.useSearchFilter(Context);

    const [testTakerSearchText, setTestTakerSearchText] = useState('');

    // חישוב כמות הפילטרים המתקדמים הפעילים כרגע (עבור התגית על כפתור "סינון מתקדם")
    const advancedFiltersCount = useMemo(() => {
        if (!schema?.advanced || !values) return 0;

        return schema.advanced.reduce((count, filter) => {
            const val = values[filter.key || filter.name];

            if (Array.isArray(val)) {
                return count + val.length; // במקרה של בחירה מרובה
            } else if (val) {
                return count + 1; // במקרה של בחירה יחידנית עם אובייקט
            }
            return count;
        }, 0);
    }, [schema?.advanced, values]);

    // בדיקה האם יש צורך לבצע קריאת שרת לשליפת מבדקים (AssessmentDefs)
    const shouldFetchAssessmentDefs = useMemo(() => {
        if (!schema) return false;

        const inPrimary = schema?.primary?.some(f => f.key === 'assessment_defs');
        const inAdvanced = schema?.advanced?.some(f => f.key === 'assessment_defs');

        return inPrimary || (isExpanded && inAdvanced);
    }, [schema, isExpanded]);

    // איפוס של כל ערכי הסינון המתקדם (מופעל מלחיצה על כפתור ה-X)
    // מאפסים כל מפתח לערך null (ולא מוחקים אותו מהאובייקט) כדי שההתנהגות תהיה זהה לניקוי שדה בודד
        const removeAdvancedFilters = useCallback((e) => {
        if (e) e.stopPropagation();
        if (!schema?.advanced) return;

        const advancedKeys = schema.advanced.map(filter => filter.key || filter.name);
        const newValues = { ...values };
        advancedKeys.forEach(key => { newValues[key] = null; });

        onChange(newValues);
    }, [schema?.advanced, values, onChange]);

    // קריאה מקושת לשרת לשליפת רשימת מבדקים (במידה ואחד הפילטרים דורש זאת)
    const assessmentDefs = useQuery({
        queryKey: [qc.assessmentDefs, headerFilters, values?.categories],
        queryFn: async ({ signal }) => {
            const { data } = await getAssessmentDefsFetch({
                headerFilters, assessmentFilters: values, signal
            });
            return data;
        },
        enabled: shouldFetchAssessmentDefs,
    });

    const testTakers = []; // מחזיק מקום כרגע

    // ריכוז כל הנתונים הנדרשים לתוך הרג'יסטרי (Registry) שמנהל את ה-Dropdowns
    const registry = filterManagerHooks.useFilterRegistry({
        assessmentDefs, testTakers, values, testTakerSearchText, setTestTakerSearchText
    });

    // מעטפת לעדכון פילטר בודד (נשלחת לפונקציית OnChange של כל פילטר)
    const onChangeFilter = useCallback((key, val) => {
        onChange({
            ...values,
            [key]: val
        });
    }, [onChange, values]);

    // פונקציית עזר המרנדרת כל שדה סינון לפי הסכימה והגדרות הרג'יסטרי שלו
    const renderFilterItem = useCallback((filterConfig) => {
        const { key, overrides = {}, customComponent: DirectComponent } = filterConfig;
        const finalConfig = { ...(registry[key] || {}), ...overrides };

        const ComponentToRender = DirectComponent || finalConfig.customComponent || AutoComplete;

        return (
            <Box key={key} sx={{ width: finalConfig.width || 'auto' }}>
                <ComponentToRender
                    {...finalConfig}
                    input={{
                        value: values?.[finalConfig.name],
                        onChange: (val) => onChangeFilter(finalConfig.name, val)
                    }}
                />
            </Box>
        );
    }, [registry, values, onChangeFilter]);

    return (
        <FilterManagerStyles advancedFiltersCount={advancedFiltersCount} isExpanded={isExpanded}>
            {/* סרגל עליון (פילטרים ראשיים וחיפוש) */}
            <div className='primary-filters'>
                {Context && (
                    <div className='search-input'>
                        <InputBase {...searchInput} border />
                        <div className='search-button'>
                            <SystemButton {...searchButton} />
                        </div>
                    </div>
                )}

                {!schema?.primary?.length || <Divider className='line' />}

                <div className='wrap-filters'>
                    <div className='filters'>
                        {schema?.primary?.map(renderFilterItem)}
                    </div>
                </div>

                {customToolbar}

                {/* אזור כפתורי פעולות (הדפסה, עמודות, כפתור פתיחת סינון מתקדם) */}
                <div className='table-header-buttons'>
                    {schema?.advanced?.length > 0 && (
                        <AdvancedBtn
                            height={35}
                            advancedFiltersCount={advancedFiltersCount}
                            removeAdvancedFilters={removeAdvancedFilters}
                            isExpanded={isExpanded}
                            setIsExpanded={setIsExpanded}
                        />
                    )}
                    {wrapperRef && (
                        <ReactToPrint
                            trigger={() =>
                                <IconButton size="small">
                                    <i className="fa-regular fa-print"></i>
                                </IconButton>
                            }
                            content={() => wrapperRef.current}
                            pageStyle={printPageStyle}
                        />
                    )}
                    {table && <ToolbarColumnsButton table={table} />}
                </div>
            </div>

            <Divider sx={{ borderColor: "#E8EEF6", width: "100%" }} />

            {/* סרגל תחתון נפתח (פילטרים מתקדמים) */}
            {schema && (
                <Collapse in={isExpanded} timeout="auto" sx={{ width: '100%' }}>
                    <div className="filters advanced-filters">
                        {schema?.advanced?.map(renderFilterItem)}
                    </div>
                    <Divider sx={{ borderColor: "#E8EEF6", width: "100%" }} />
                </Collapse>
            )}
        </FilterManagerStyles>
    );
};

export default FilterManager;